import {Component, OnInit} from '@angular/core';
import {
  CtBinaryOperator,
  CtExportConfiguration,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelOperator,
  MAT_RAISED_PRIMARY,
} from "@ctsolution/ct-framework";
import {CTMGeneralService} from "../../../_core/lib/general.service";
import {ActivatedRoute} from "@angular/router";
import {CompanyRouteData} from "../../../_core/route-data/company.route-data";
import {DEFAULT_DATATABLE_CONFIGURATION} from "../../../_core/constants";
import {CompanyService} from "../company.service";
import {CustomerType} from "../../../_core/enum/customer-type";
import {CtManagerService} from "../../../_core/lib/ct-manager.service";
import {CTConfigurationModel} from "../../../_core/classes/ct-configuration-model";
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-company-list',
  template: `
    <ct-model *ngIf="configuration" [configuration]="configuration"></ct-model>`,
})
export class CompanyListComponent implements OnInit {

  configuration: CtModelConfiguration<CompanyListComponent> | null = null;

  // Configurazione per controllare la visibilità dell'export
  companyExportConfiguration: CTConfigurationModel = CTConfigurationModel.create().setVisible(false);

  // Configurazione custom della lista clienti: filtri costanti e operazioni dal MenuJson
  customCompanyListConfiguration: CTConfigurationModel = CTConfigurationModel.create();

  constructor(
    private general: CTMGeneralService,
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private managerService: CtManagerService,
    private translate: TranslateService) {
  }

  async ngOnInit() {

    // ✅ Recupera la configurazione override per company export
    await this.managerService.getComponentOverride("company", "companyExportConfiguration", this.companyExportConfiguration).then((component) => {
      return;
    });

    // Configurazione custom della lista (filtri costanti, operazioni) dichiarata nel MenuJson
    await this.managerService.getComponentOverride("company", "customCompanyListConfiguration", this.customCompanyListConfiguration).then((component) => {
      return;
    });

    const configuration = CtModelConfiguration
      .create<CompanyListComponent>()
      .setRouteData(CompanyRouteData())
      .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION())
      .setOperations([
        CtModelOperator.Create,
        CtModelOperator.FilterCustomToggle,
        ...(this.companyExportConfiguration.visible ? [CtModelOperator.Export] : [])  // ✅ Abilita export solo se visible
      ])
      .setCustomToggleLabel('Nascondi disabilitati')
      .setCustomToggleInitialState(true)
      .setCustomTogglePersistState(true)
      .setOnCustomToggle((active: boolean) => {
        
        if (active) {
          
          configuration.filterValues.upsertConstantFilter(
            CTModelDatatableFilter.create()
              .setField('Active') // Campo da filtrare
              .setValue(true)   // Valore del filtro
              .setOperatorType(CtBinaryOperator.Equal) // Operatore
          );
          
        } else {
          
          configuration.filterValues.removeConstantByField('Active');
        
        }
        
      })

    const typePathValue: string | null = this.route.snapshot.paramMap.get("type");

    if (!typePathValue) return;

    const isSupplier = this.companyService.getCustomerTypeByRouteValue(typePathValue) === CustomerType.Supplier;

    configuration
      .filterValues
      .addConstantFilter(
        CTModelDatatableFilter
          .create()
          .setField(isSupplier ? 'IsSupplier' : 'IsCustomer')
          .setValue(true)
          .setOperatorType(CtBinaryOperator.Equal)
      )

    if (isSupplier) {

      this
        .route
        .data
        .subscribe(data => {
          data["title"] = "CT_MENU.supplier";
        });

    }

    // ✅ Configurazione Export SOLO se companyExportConfiguration.visible è true
    // Differenzia tra Supplier e Customer
    if (this.companyExportConfiguration.visible) {

      const label = this.translate.instant(isSupplier ? 'CT_EXPORT.exportSuppliers' : 'CT_EXPORT.exportCustomers');

      configuration
        .setExportConfiguration(
          CtExportConfiguration
            .create()
            // Action backend: diversa per Supplier e Customer
            .setAction(isSupplier ? 'ExportExcel_Company_Private' : 'ExportExcel_Company_Business')
            // Label bottone
            .setLabel('Esporta')
            // Icona bottone
            .setIcon('download')
            // Nome file per il download (senza estensione)
            .setFileName(label)
            // Colore bottone blu
            .setMaterialOptions(MAT_RAISED_PRIMARY)
            // Esporta tutti i record ignorando la paginazione
            .setIncludePagination(false)
        );
    }

    configuration
      .setOnCreate(() => this.general.navigateOnCTModelRouter(["customer", typePathValue], "create", {qpHandling: 'preserve'}))
      .setOnEdit(event => this.general.navigateOnCTModelRouter(["customer", typePathValue], "edit", {
        value: event,
        configuration: this.configuration!,
        qpHandling: 'preserve'

      }));

    this.configuration = configuration;

    // Filtri costanti e operazioni dichiarati per-client nel MenuJson.
    this.applyMenuJsonListOverrides();

  }

  /**
   * Applica alla lista i filtri costanti, l'azione massiva e l'override delle operazioni dichiarati
   * per-client nel MenuJson (nodo "customCompanyListConfiguration"). Se il JSON non li dichiara non
   * cambia nulla: gli altri client restano con la lista di oggi.
   */
  private applyMenuJsonListOverrides(): void {

    const configuration = this.configuration;

    if (!configuration) return;

    // upsert e non add: se il campo è già filtrato dalla lista, vince quanto dichiara il JSON.
    (this.customCompanyListConfiguration.constantFilters ?? [])
      .forEach(filter => configuration
        .filterValues
        .upsertConstantFilter(
          CTModelDatatableFilter
            .create()
            .setField(filter.field)
            .setValue(filter.value)
            .setOperatorType(filter.operatorType ?? CtBinaryOperator.Equal)));

    // Azione massiva sui clienti selezionati (es. DTS: "Disabilita selezionati"), solo se il JSON la dichiara
    this.managerService.applyMenuJsonBulkAction(configuration, this.customCompanyListConfiguration.bulkAction);

    const operations = this.customCompanyListConfiguration.datatableOperations;

    if (!operations) return;

    configuration.setOperations(operations);

    // Il merge col config del backend (getFields) reintroduce le Operations del server:
    // senza questa riapplicazione l'eliminazione tornerebbe disponibile.
    configuration
      .initialized()
      .subscribe(() => configuration.setOperations(operations));

  }

}
